import React, { FC } from 'react';
import * as Yup from 'yup';
import { Trans, useTranslation } from 'react-i18next';
import { Field } from 'formik';

import { BaseEditorDialogProps, EditorDialog } from 'components/dialog/EditorDialog';
import { TextField } from 'components/form/text-field/TextField';

export interface TableViewCreationFormValues {
  name: string;
}

export interface TableViewCreationDialogProps
  extends BaseEditorDialogProps<TableViewCreationFormValues> {
  existingNames: string[];
  onSaveView: (name: string) => Promise<void>;
}

export const TableViewCreationDialog: FC<TableViewCreationDialogProps> = ({
  existingNames,
  onSaveView,
  selected,
  ...props
}) => {
  const { t } = useTranslation();

  const onSubmit = async ({ name }: TableViewCreationFormValues) => {
    await onSaveView(name.trim());
  };

  return (
    <EditorDialog
      initialValues={{ name: selected?.name ?? '' }}
      maxWidth="sm"
      onSubmit={onSubmit}
      validationSchema={Yup.object().shape({
        name: Yup.string()
          .required()
          .min(2)
          .max(255)
          .test('unique-name', t('TABLE.VIEW_NAME_EXISTS'), (value) => {
            if (!value) {
              return true;
            }
            return !existingNames.some((name) => name.toLowerCase() === value.trim().toLowerCase());
          }),
      })}
      title={<Trans i18nKey="TABLE.SAVE_VIEW" />}
      cancelCustomTestId="table-view-save-cancel"
      submitCustomTestId="table-view-save-submit"
      {...props}
    >
      <Field
        component={TextField}
        name="name"
        label={<Trans i18nKey="COMMON.NAME" />}
        customTestId="table-view-name-input"
        autoFocus
      />
    </EditorDialog>
  );
};
